/**
 * @license CKCode
 * Copyright 2023 CourseKata
 * SPDX-License-Identifier: GPL-3.0-or-later
 */
import { __decorate } from "tslib";
import "@material/web/button/filled-button.js";
import "@material/web/button/filled-tonal-button.js";
import "@material/web/icon/icon.js";
import "@material/web/iconbutton/icon-button.js";
import "@material/web/progress/linear-progress.js";
import "../../editor/editor.js";
import "../../message-area/feedback-area.js";
import "../../message-area/status-area.js";
import "../../output-area/output-area.js";
import { LitElement, html, nothing } from "lit";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";
import utils from "markdown-it/lib/common/utils.js";
import deepmerge from "ts-deepmerge";
import { KernelLanguage, KernelStatus, LogLevel, RequestType, ServerStatus, } from "../../core/interfaces/index.js";
import { NamedLogger } from "../../core/logging.js";
import { SessionInteractor } from "../../core/session-interactor.js";
import { isStatus } from "../../core/status.js";
import { dedent } from "../../processors/formatting.js";
import { TestwhatAdapterResolver } from "../../processors/language-adapters/resolver.js";
import { createSession } from "../../session/session-factory.js";
import { BrowserStorage } from "../../storage/browser-storage.js";
import { renderMarkdown, watchForNode } from "../../utils.js";
import { defaultConfig, isConfig, isDefaultConfig } from "../config.js";
import { makeListener, parseKeyBindings } from "../key-binding.js";
import { EventEmitter } from "./event-emitter.js";
import { logos } from "./logos.js";
const SUBMITTING_MESSAGE = "Submitting...";
export class CodeCell extends LitElement {
    /**
     * Set a new default configuration for all cells on a page.
     *
     * Applies the given configuration to every cell on the page by settings
     * {@linkcode ICodeCell.config}. If the `watch` parameter is `true`, the configuration will also
     * be applied to any new cells that are created after the function is called.
     *
     * @param partialConfig the new default configuration (will be merged with defaults)
     * @param watch whether to watch for new cells and apply the configuration to them
     * @param withReload whether to reload the cells from storage after applying the configuration
     */
    static globalConfigure(partialConfig, watch = false, withReload = true) {
        const config = deepmerge(defaultConfig, partialConfig);
        const cells = document.querySelectorAll(this.tag);
        cells.forEach((cell) => {
            cell.config = config;
            if (withReload)
                cell.loadFromStorage();
        });
        if (watch) {
            watchForNode((node) => node instanceof Element && node.tagName.toLowerCase() === this.tag, (node) => (node.config = config));
        }
    }
    /**
     * Convert all matching source elements to `CodeCell`s (see {@linkcode extractSpecFromMarkdown()}).
     *
     * To make code cells more readable in markdown files, you can specify them using [fenced code
     * blocks][fenced-code-blocks]. These render to HTML as `<pre><code> CONTENT </code></pre>` where
     * the content can then be processed by this function. The content should have a separate header
     * for each kind of code you want to initialize the cell with ({@linkcode IExerciseSpec}). Here is an
     * example specification:
     *
     * [fenced-code-blocks]: https://www.markdownguide.org/extended-syntax/#fenced-code-blocks
     *
     * ````markdown
     * ```{ #cell-id data-code-cell=true }
     * %%% setup
     * library(coursekata)
     *
     * %%% prompt
     * # this gets shown when the cell is first loaded or when you press Reset
     * ```
     * ````
     *
     * Note that the headers for setup and prompt are preceded by `%%%` and that the top fence has
     * braces (`{}`) to give the cell an ID and to indicate that it is a code cell. This code block
     * will be parsed to this by most markdown renderers (some might put the attributes on the code
     * tag instead of the pre tag, like markdown-it):
     *
     * ```html
     * <pre id="cell-id" data-code-cell="true">
     *   <code>
     *     %%% setup
     *     library(coursekata)
     *
     *     %%% prompt
     *     # this gets shown when the cell is first loaded or when you press Reset
     *   </code>
     * </pre>
     * ```
     *
     * Finally, this method will read that rendered markdown and replace it with the appropriate
     * `<code-cell`>:
     *
     * ```html
     * <code-cell id="cell-id">
     *   <code data-type="setup">
     *     library(coursekata)
     *   </code>
     *   <code data-type="prompt">
     *     # this gets shown when the cell is first loaded or when you press Reset
     *   </code>
     * </code-cell>
     * ```
     *
     * @param selector the CSS selector for the `<pre>` tags to read from. The easiest way to
     *   ensure that the CSS selector is unique to `<code-cell>` specifications is to give them all a
     *   `data-` attribute like `data-code-cell=true`.
     */
    static globalFromMarkdown(selector = "[data-ckcode]") {
        document.querySelectorAll(selector).forEach((source) => {
            const sourceIsPre = source.tagName.toLowerCase() === "pre";
            const parentIsPre = !sourceIsPre && source.parentElement?.tagName.toLowerCase() === "pre";
            if (!sourceIsPre && !parentIsPre)
                return;
            const codeCell = document.createElement(this.tag);
            codeCell.id = source.id;
            CodeCell.loadDatasetAttributes(codeCell, source);
            const codeContainingCell = sourceIsPre ? source.querySelector("code") : source;
            const code = this.extractSpecFromMarkdown(codeContainingCell.innerText);
            Object.entries(code).forEach((entry) => {
                const code = document.createElement("code");
                code.dataset["type"] = entry[0];
                code.textContent = entry[1] ? entry[1].toString() : "";
                codeCell.append(code);
            });
            sourceIsPre ? source.replaceWith(codeCell) : source.parentElement.replaceWith(codeCell);
        });
    }
    static loadDatasetAttributes(codeCell, source) {
        codeCell.disableSubmit = source.dataset["submittable"] === "false";
        const language = source.dataset["language"]?.toLowerCase();
        if ([KernelLanguage.python, KernelLanguage.r].includes(language))
            codeCell.language = language;
    }
    /**
     * Extract code elements from the text of a code-cell specified via markdown.
     * @param text the text to parse (see {@linkcode globalFromMarkdown} for specification)
     */
    static extractSpecFromMarkdown(text) {
        const spec = {};
        let currentHeader;
        text.split("\n").forEach((line) => {
            const allowedHeaders = ["setup", "prompt", "test", "solution"];
            const header = line.match(/^\s*%%%\s*(\w+)\s*$/);
            const isHeader = header?.length === 2 && allowedHeaders.includes(header[1]);
            if (isHeader) {
                currentHeader = header[1]; // can cast because checked allowedHeaders
                return;
            }
            // not on a header line, but also no current header is known
            // so... we wouldn't know where to put this content
            if (!currentHeader)
                return;
            if (!spec[currentHeader]) {
                spec[currentHeader] = line;
                return;
            }
            else {
                spec[currentHeader] += "\n" + line;
                return;
            }
        });
        // trim empty keys
        Object.keys(spec).forEach((key) => {
            if (!spec[key]?.trim())
                delete spec[key];
        });
        return spec;
    }
    get language() {
        return this._language ?? this.config.session.language;
    }
    set language(language) {
        this._language = language;
        this.config.session.language = language;
        this.changeLanguage(this.language);
    }
    get disableSubmit() {
        return this._disableSubmit ?? this.config.disableSubmit ?? false;
    }
    set disableSubmit(disableSubmit) {
        this._disableSubmit = disableSubmit;
        this.config.disableSubmit = disableSubmit;
    }
    /** Setup the cell's logger. */
    connectLogger() {
        this.log = this.log ?? new NamedLogger("ckcode");
        this.log.logLevel = this.config.debug ? LogLevel.debug : LogLevel.info;
    }
    /** Setup the cell's storage. */
    connectStorage() {
        const prefixParts = ["ckcode", this.config.storagePrefix ?? "", this.id ?? ""];
        const prefix = `${prefixParts.join("--")}--`;
        this.storage = new BrowserStorage({ prefix }, this.log);
    }
    storedRun() {
        return this.storage.getItem("run");
    }
    /** Retrieve last run code. */
    get storedCode() {
        return this.storedRun()?.code;
    }
    /** Retrieve last rendered output. */
    get storedOutput() {
        return this.storedRun()?.outputs;
    }
    /** Retrieve last rendered feedback. */
    get storedFeedback() {
        const lastRun = this.storedRun();
        return lastRun?.type === "submit" ? lastRun?.feedback : null;
    }
    /** Load the cell's state from storage. */
    loadFromStorage() {
        if (!this.editor || !this.outputArea || !this.feedbackArea)
            return;
        this.editor.text = this.storedCode ?? this.spec.prompt ?? "";
        this.feedbackArea.feedback = this.storedFeedback ?? undefined;
        this.outputArea.clear();
        (this.storedOutput ?? []).forEach((output) => this.handleOutput(output));
    }
    get status() {
        return this._status;
    }
    set status(status) {
        if (!isStatus(status))
            throw new Error("Invalid status DTO.");
        this._status = status;
    }
    get feedback() {
        return this._feedback;
    }
    get config() {
        return this._config;
    }
    set config(config) {
        const mergedConfig = deepmerge(defaultConfig, this._config, config);
        isDefaultConfig(mergedConfig);
        if (utils.isString(mergedConfig.highContrast)) {
            const setting = mergedConfig.highContrast.toLowerCase().trim();
            mergedConfig.highContrast = setting === "auto" ? "auto" : setting === "true";
        }
        if (mergedConfig.debug && mergedConfig.session.debug !== true) {
            mergedConfig.session.debug = true;
        }
        this._config = mergedConfig;
        this.connectSession();
        this.connectLogger();
        this.connectStorage();
        this.requestUpdate();
    }
    /** Hookup the session to the cell and vice versa. */
    connectSession() {
        if (!this.sessionInteractor && isConfig(this.config, false, this.log)) {
            const testwhatAdapter = new TestwhatAdapterResolver();
            this.session = createSession(this.config.session);
            this.sessionInteractor = new SessionInteractor(this.session, testwhatAdapter, this.log);
            this.session.serverStatusChanged.connect(this.handleServerStatusChange, this);
            this.session.kernelStatusChanged.connect(this.handleKernelStatusChange, this);
        }
    }
    handleServerStatusChange(session, status) {
        if ([ServerStatus.building, ServerStatus.launching].includes(status)) {
            const title = status.charAt(0).toUpperCase() + status.slice(1);
            const message = `${title}: please don't refresh or close this tab`;
            this.status = { ...this.status, connectionStatus: status, message };
        }
        else {
            delete this.status.message;
            this.status = { ...this.status, connectionStatus: status };
        }
    }
    handleKernelStatusChange(session, status) {
        this.status = { ...this.status, kernelStatus: status };
    }
    /** Initialize the session connection. */
    async connect() {
        if (this.config.session.disabled)
            return;
        this.status = {
            connectionStatus: ServerStatus.connecting,
            kernelStatus: KernelStatus.disconnected,
        };
        this.connectSession();
        if (!this.sessionInteractor) {
            const error = "No session to connect to";
            this.log.error(error);
            this.status = {
                connectionStatus: ServerStatus.disconnected,
                kernelStatus: KernelStatus.disconnected,
                message: `Connection error: ${error}`,
            };
            return;
        }
        try {
            return await this.sessionInteractor?.connect();
        }
        catch (error) {
            this.log.error(error);
            this.status = {
                connectionStatus: ServerStatus.disconnected,
                kernelStatus: KernelStatus.disconnected,
                message: `Connection error: ${error.message}`,
            };
        }
    }
    focusActive() {
        const activeCell = document.getElementById(this.session?.activeRequester.id);
        activeCell?.scrollIntoView({ behavior: "smooth" });
        activeCell?.focus();
    }
    /** Request code execution in the session. */
    async run() {
        if (!this.isRunnable())
            return;
        const code = this.editor.text;
        this.feedbackArea?.clear();
        this.outputArea?.clear();
        this.status = { ...this.status, kernelStatus: KernelStatus.busy };
        this.events.run.emit({ content: code });
        const request = {
            type: RequestType.run,
            code: code,
            language: this.language,
            spec: this.spec,
            requester: this,
        };
        try {
            const response = await this.sessionInteractor.run(request);
            this.storeResponse(response);
            response.outputs.forEach((output) => this.handleOutput(output));
        }
        catch (error) {
            this.log.error(error);
        }
    }
    /**
     * Whether the cell is runnable.
     * @note This is only a function because accessors cannot be typeguards.
     */
    isRunnable() {
        return Boolean(!this.config.session.disabled &&
            !this.interrupting &&
            this.sessionInteractor &&
            this.editor &&
            this.outputArea &&
            (this.session.changingLanguage || this.status.kernelStatus === KernelStatus.ready));
    }
    /**
     * Handle output from the session.
     * @param output the output to handle
     */
    handleOutput(output) {
        if (!this.outputArea)
            return;
        this.outputArea.addOutput(output);
    }
    /**
     * Store the response from the session, if it isn't overly large.
     * @param response the response to store
     */
    storeResponse(response) {
        const toStore = { ...response };
        delete toStore.requester;
        if (this.storage.estimateSize("run", toStore) > this.storage.totalSize * 0.05) {
            delete toStore.outputs;
            this.log.warn("Response too large to store, skipping outputs");
        }
        this.storage.setItem("run", toStore);
    }
    /** Request code execution and testing in the session. */
    async submit() {
        if (!this.isSubmittable())
            return;
        const code = this.editor.text;
        this.feedbackArea?.clear();
        this.outputArea?.clear();
        this.status = { ...this.status, kernelStatus: KernelStatus.busy };
        this.events.submit.emit({ content: code });
        const request = {
            type: RequestType.submit,
            code: code,
            language: this.language,
            spec: this.spec,
            requester: this,
        };
        try {
            this.submitting = true;
            const response = await this.sessionInteractor.submit(request);
            this.storeResponse(response);
            response.outputs.forEach((output) => this.handleOutput(output));
            this.handleFeedback(response.feedback);
        }
        catch (error) {
            this.log.error(error);
            this.status.message = "There was an error submitting your code. Please try again.";
        }
        finally {
            this.submitting = false;
            if (this.status.message === SUBMITTING_MESSAGE) {
                delete this.status.message;
            }
        }
    }
    /**
     * Whether the cell is submittable.
     * @note This is only a function because accessors cannot be typeguards.
     */
    isSubmittable() {
        return Boolean(this.isRunnable() && !this.disableSubmit && Boolean(this.spec.test?.trim()));
    }
    /**
     * Handle feedback from the session.
     * @param feedback the feedback to handle
     */
    handleFeedback(feedback) {
        delete this.status.message;
        if (!this.feedbackArea)
            return;
        feedback.broken = Boolean(feedback.broken);
        feedback.correct = Boolean(feedback.correct);
        this.feedbackArea.feedback = feedback;
        if (feedback.broken)
            return;
        this.events.feedback.emit(feedback);
    }
    /** Request an interrupt of the session. */
    async interrupt() {
        this.status = { ...this.status, kernelStatus: KernelStatus.busy, message: "Interrupting..." };
        this.interrupting = true;
        await this.sessionInteractor?.interrupt({ requester: this });
        this.interrupting = false;
        this.handleKernelStatusChange(this.session, this.session?.kernelStatus);
        this.handleServerStatusChange(this.session, this.session?.serverStatus);
    }
    /** Reset the code in the cell's editor. */
    async reset() {
        this.events.reset.emit();
        if (this.session?.serverStatus === ServerStatus.connected) {
            this.sessionInteractor?.clearState({ requester: this });
        }
        this.storage.removeItem("run");
        this.storage.removeItem("submit");
        // clear view
        this.outputArea?.clear();
        this.feedbackArea?.clear();
        if (this.editor)
            this.editor.text = this.spec.prompt ?? "";
        this.requestUpdate();
    }
    /** Replace the code in the cell's editor with the solution. */
    async solution() {
        if (!this.editor || !this.spec.solution || !this.config.enableSolution)
            return;
        this.events.solution.emit();
        this.editor.text = this.spec.solution;
    }
    async changeLanguage(language) {
        await this.sessionInteractor?.changeLanguage({ language, requester: this });
        this.handleServerStatusChange(this.session, this.session?.serverStatus);
        this.handleKernelStatusChange(this.session, this.session?.kernelStatus);
    }
    // Private API
    constructor() {
        super();
        // internal State
        this.active = false;
        this.submitting = false;
        this.interrupting = false;
        this._status = {
            connectionStatus: ServerStatus.disconnected,
            kernelStatus: KernelStatus.disconnected,
        };
        this._config = defaultConfig;
        this.prefersHighContrast = false;
        this.spec = {};
        this.started = false;
        this.keybindings = [
            {
                key: "Ctrl-Enter",
                mac: "Meta-Enter",
                run: () => this.runButtonRef.value?.click(),
                shift: () => this.submitButtonRef.value?.click(),
                preventDefault: true,
            },
            {
                key: "Alt-Enter",
                run: () => this.runButtonRef.value?.click(),
                shift: () => this.submitButtonRef.value?.click(),
                preventDefault: true,
            },
            {
                key: "Ctrl-i",
                mac: "Meta-i",
                run: () => this.interruptButtonRef.value?.click(),
                preventDefault: true,
            },
            {
                key: "Ctrl-e",
                mac: "Meta-e",
                run: () => this.editor?.focus(),
                preventDefault: true,
            },
        ];
        // events and actions
        /** Events emitted by the cell when the user performs an action. */
        this.events = {
            start: new EventEmitter(this, "start"),
            run: new EventEmitter(this, "run"),
            submit: new EventEmitter(this, "submit"),
            feedback: new EventEmitter(this, "feedback"),
            solution: new EventEmitter(this, "solution"),
            reset: new EventEmitter(this, "reset"),
        };
        this.editorRef = createRef();
        this.statusBarRef = createRef();
        this.feedbackAreaRef = createRef();
        this.connectButtonRef = createRef();
        this.runButtonRef = createRef();
        this.submitButtonRef = createRef();
        this.interruptButtonRef = createRef();
        this.activeLocatorRef = createRef();
        this.resetButtonRef = createRef();
        this.solutionButtonRef = createRef();
        this.outputAreaRef = createRef();
        this.connectLogger();
        this.connectStorage();
        // ask for the correct language when the cell is focused
        this.addEventListener("focusin", () => {
            if (!this.started) {
                this.started = true;
                this.events.start.emit();
            }
        }, { once: true });
        // listen for keybindings
        // TODO: this only works for the simple default keybindings. It would need a lot of work to be
        // robust to user-defined keybindings.
        this.parsedKeyBindings = parseKeyBindings(this.keybindings);
        this.addEventListener("keydown", makeListener(this.parsedKeyBindings, (fn) => {
            const ancestorEditor = this.shadowRoot?.activeElement?.closest("ck-editor");
            fn();
            ancestorEditor?.focus();
        }, (el) => this.updateComplete.then(() => el.focus())), true);
    }
    createRenderRoot() {
        const root = super.createRenderRoot();
        // when the user interacts with the cell, make sure the kernel is the right language
        // - don't do this for the run or submit buttons because they already do the check
        root.addEventListener("focusin", (e) => {
            if (this.status.connectionStatus === ServerStatus.connected &&
                this.runButtonRef.value !== e.target &&
                this.resetButtonRef.value !== e.target &&
                this.submitButtonRef.value !== e.target &&
                this.interruptButtonRef.value !== e.target &&
                this.activeLocatorRef.value !== e.target) {
                this.changeLanguage(this.language).catch((e) => this.log.warn(e));
            }
        });
        return root;
    }
    connectedCallback() {
        super.connectedCallback();
        this.connectSession();
        this.connectStorage();
        // load dataset attributes
        CodeCell.loadDatasetAttributes(this, this);
        // extract code
        this.querySelectorAll("code").forEach((element) => {
            const type = element.dataset["type"];
            const content = element.textContent;
            element.remove();
            if (content && type && ["setup", "prompt", "solution", "test"].includes(type)) {
                this.spec[type] = dedent(content);
            }
        });
        // detect whether the user prefers high contrast
        if (this.config.highContrast !== "auto") {
            this.prefersHighContrast = this.config.highContrast;
        }
        else {
            const highContrastQuery = "(forced-colors: active), (prefers-contrast: more)";
            const highContrastMatcher = window.matchMedia(highContrastQuery);
            this.prefersHighContrast = highContrastMatcher.matches;
            highContrastMatcher.addEventListener("change", () => {
                this.prefersHighContrast = highContrastMatcher.matches;
            });
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.sessionInteractor?.disconnect();
    }
    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.loadFromStorage();
    }
    render() {
        return html `
      <main class=${classMap({ "high-contrast": this.prefersHighContrast })}>
        <div class="editor">${this.renderEditor()} ${this.renderStatusBar()}</div>
        ${this.renderFeedbackArea()} ${this.renderStatusMessage()}
        <div class="control-panel">
          <div class="control-group control-group--start">${this.renderSessionControls()}</div>
          <div class="control-group control-group--end">${this.renderEditorControls()}</div>
        </div>
        ${this.renderOutputArea()}
      </main>
      ${this.renderFooter()}
    `;
    }
    get editor() {
        return this.editorRef.value;
    }
    renderEditor() {
        return html `<ck-editor
      class=${classMap({ "high-contrast": this.prefersHighContrast })}
      ${ref(this.editorRef)}
      theme=${this.config.theme ?? "auto"}
      language=${this.language}
    ></ck-editor>`;
    }
    get statusBar() {
        return this.statusBarRef.value;
    }
    renderStatusBar() {
        if (this.interrupting ||
            this.submitting ||
            this.status.connectionStatus === ServerStatus.building ||
            this.status.connectionStatus === ServerStatus.launching ||
            this.status.connectionStatus === ServerStatus.connecting ||
            this.status.kernelStatus === KernelStatus.busy ||
            this.status.kernelStatus === KernelStatus.connecting) {
            return html `<md-linear-progress
        class=${classMap({ interrupting: this.interrupting })}
        indeterminate
      ></md-linear-progress>`;
        }
    }
    get feedbackArea() {
        return this.feedbackAreaRef.value;
    }
    renderFeedbackArea() {
        return html `<ck-feedback-area
      ${ref(this.feedbackAreaRef)}
      class=${classMap({
            hidden: !this.feedbackArea || !this.feedbackArea.feedback,
            "high-contrast": this.prefersHighContrast,
        })}
      .dto=${this.feedback}
    ></ck-feedback-area>`;
    }
    renderStatusMessage() {
        if (this.submitting) {
            this.status.message = SUBMITTING_MESSAGE;
        }
        if (this.status.message) {
            return html `<ck-status-area
        class=${classMap({ "high-contrast": this.prefersHighContrast })}
        type="warning"
        message=${this.status.message}
      ></ck-status-area>`;
        }
    }
    renderSessionControls() {
        if (this.config.session.disabled) {
            return html `${this.config.session.disabledMessage}`;
        }
        if (this.interrupting) {
            return this.renderRunButton(true);
        }
        if (this.status.connectionStatus !== ServerStatus.connected) {
            return this.renderConnectButton();
        }
        const isBusy = this.status.kernelStatus === KernelStatus.busy;
        if ((isBusy || this.submitting) && this.active && !this.session?.changingLanguage) {
            return [this.renderInterruptButton()];
        }
        const runnable = this.isRunnable();
        return [
            this.renderRunButton(!runnable || isBusy),
            this.isSubmittable() ? this.renderSubmitButton(!runnable || isBusy) : nothing,
            isBusy && !this.active ? this.renderActiveLocator() : nothing,
        ];
    }
    renderConnectButton() {
        return html `<md-filled-button
      ${ref(this.connectButtonRef)}
      @click=${this.connect}
      ?disabled=${this.status.connectionStatus !== "disconnected"}
      >Connect
    </md-filled-button>`;
    }
    renderRunButton(disabled) {
        return html `<md-filled-button ${ref(this.runButtonRef)} @click=${this.run} ?disabled=${disabled}
      >Run
    </md-filled-button>`;
    }
    renderSubmitButton(disabled) {
        return html `<md-filled-tonal-button
      ${ref(this.submitButtonRef)}
      @click=${this.submit}
      ?disabled=${disabled}
      >Submit
    </md-filled-tonal-button>`;
    }
    renderInterruptButton() {
        return html `<md-filled-tonal-button ${ref(this.interruptButtonRef)} @click=${this.interrupt}
      >Interrupt
    </md-filled-tonal-button>`;
    }
    renderActiveLocator() {
        if (this.active)
            return;
        const activeCell = document.getElementById(this.session.activeRequester.id);
        if (!activeCell)
            return;
        return html `<md-icon-button
      ${ref(this.activeLocatorRef)}
      @click=${this.focusActive}
      title="Go to busy cell (#${activeCell.id})"
    >
      <md-icon aria-hidden="true">jump_to_element</md-icon>
    </md-icon-button>`;
    }
    renderEditorControls() {
        const outputs = [];
        if (this.config.enableSolution && this.spec.solution && this.spec.solution.trim() !== "") {
            outputs.push(html `<md-filled-tonal-button ${ref(this.solutionButtonRef)} @click=${this.solution}>
          Solution
        </md-filled-tonal-button>`);
        }
        outputs.push(html `<md-filled-tonal-button ${ref(this.resetButtonRef)} @click=${this.reset}>
        Reset
      </md-filled-tonal-button>`);
        return outputs;
    }
    get outputArea() {
        return this.outputAreaRef.value;
    }
    renderOutputArea() {
        return html `<ck-output-area
      ${ref(this.outputAreaRef)}
      class=${classMap({
            hidden: !this.outputArea || this.outputArea.outputs.length === 0,
            "high-contrast": this.prefersHighContrast,
        })}
    ></ck-output-area>`;
    }
    renderFooter() {
        const id = this.renderExerciseId();
        if (id || this.config.helpLink) {
            return html `<footer class=${classMap({ "high-contrast": this.prefersHighContrast })}>
        <div class="footer-panel">${id}</div>
        <div class="footer-panel footer-panel--right">
          ${this.renderKernelLogo()}
          <div class="help-link">${unsafeHTML(renderMarkdown(this.config.helpLink, true))}</div>
        </div>
      </footer>`;
        }
    }
    renderExerciseId() {
        if (this.config.hideExerciseId)
            return nothing;
        return html `<div class="exercise-id">#${this.id}</div>`;
    }
    renderKernelLogo() {
        return logos[this.language] ?? nothing;
    }
}
CodeCell.tag = "code-cell";
__decorate([
    state()
], CodeCell.prototype, "_language", void 0);
__decorate([
    property({ type: String })
], CodeCell.prototype, "language", null);
__decorate([
    property({ type: Boolean, attribute: "disable-submit" })
], CodeCell.prototype, "disableSubmit", null);
__decorate([
    state()
], CodeCell.prototype, "active", void 0);
__decorate([
    state()
], CodeCell.prototype, "submitting", void 0);
__decorate([
    state()
], CodeCell.prototype, "interrupting", void 0);
__decorate([
    state()
], CodeCell.prototype, "_status", void 0);
__decorate([
    state()
], CodeCell.prototype, "_feedback", void 0);
__decorate([
    state()
], CodeCell.prototype, "_config", void 0);
__decorate([
    state()
], CodeCell.prototype, "prefersHighContrast", void 0);
//# sourceMappingURL=code-cell.js.map